import React, { useEffect, useState } from "react";
import { Alert, Box, Collapse, Skeleton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  fetchCreditPaymentRecords,
  fetchCreditQuotaStatements,
  fetchOriginalPaymentPlan,
  fetchQuota,
  fetchQuotaPaidComponents,
  fetchQuotaProjectedComponents,
} from "../gestionCarteraApi";
import {
  appliedPaymentDetails,
  appliedPaymentRecords,
  formatDate,
  formatMoney,
  isMoneyAmount,
  originalPlanQuota,
  quotaBalance,
  quotaComparisonRows,
  quotaComponentSections,
  quotaState,
  toComponentRows,
  todayIso,
} from "../gestionCarteraFormat";
import { COLORS, labelSx, ModalShell, SecondaryButton, StatusBadge } from "../gestionCarteraUi";

const amountCellSx = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
const paymentHeadSx = { fontSize: 12, fontWeight: 500, color: COLORS.muted, py: 0.75, whiteSpace: "nowrap" };
const IDLE = { status: "idle", data: null };

const paymentAmount = (payment) => payment?.appliedValue ?? payment?.paymentValue ?? payment?.value;

const paymentWhen = (payment) => payment?.paymentDate || payment?.applicationDate;

const COMPARISON_FILL = {
  blue: "rgba(22, 30, 85, 0.045)",
  green: "rgba(168, 200, 175, 0.24)",
};

function MoneyTable({ title, description, rows, total, fill }) {
  return (
    <Box component="section" aria-label={title} sx={{ height: "100%" }}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary, minHeight: 24 }}>{title}</Typography>
      {description && (
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, mt: 0.5, mb: 1.5, minHeight: 40 }}>
          {description}
        </Typography>
      )}
      <Box sx={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: COMPARISON_FILL[fill] || "#fff",
        "& td": { backgroundColor: "transparent" },
      }}>
        <Table size="small" aria-label={title}>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell sx={{ height: 36, py: 0, whiteSpace: "nowrap", fontWeight: row.bold ? 700 : undefined }}>{row.label}</TableCell>
                <TableCell sx={{ ...amountCellSx, height: 36, py: 0, whiteSpace: "nowrap", fontWeight: row.bold ? 700 : undefined, color: row.muted ? COLORS.muted : COLORS.text }}>{row.display}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {total != null && (
            <TableFooter>
              <TableRow sx={{ "& td": { fontWeight: 500, fontSize: 14, color: COLORS.primary, backgroundColor: COLORS.rowHover } }}>
                <TableCell>Total</TableCell>
                <TableCell sx={amountCellSx}>{formatMoney(total)}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Box>
    </Box>
  );
}

function ResourceTable({ title, description, resource, keyName }) {
  if (resource.status === "loading" || resource.status === "idle") return <Skeleton variant="rounded" height={220} />;
  if (resource.status === "error") return <Alert severity="error">{`No pudimos cargar ${title.toLowerCase()}.`}</Alert>;
  const { rows, total } = toComponentRows(resource.data, keyName);
  return (
    <MoneyTable
      title={title}
      description={description}
      total={total}
      rows={rows.map((row) => ({ label: row.label, display: formatMoney(row.value), muted: !(row.value > 0) }))}
    />
  );
}

const loadResource = (loader, setResource) => {
  setResource({ status: "loading", data: null });
  loader()
    .then((data) => setResource({ status: "ready", data }))
    .catch(() => setResource({ status: "error", data: null }));
};

const comparisonCell = (type, value) => ({
  display: type === "date" ? formatDate(value) : formatMoney(value),
  muted: type === "money" && (!isMoneyAmount(value) || Number(value) === 0),
});

/**
 * Plan original de esta cuota, cómo quedó aplicada y los payment records aplicados.
 */
export default function ComponentesCuotaModal({ open, quota, quotasCount, creditUuid, clientUuid, onClose }) {
  const today = todayIso();
  const [paid, setPaid] = useState(IDLE);
  const [quotaDetail, setQuotaDetail] = useState(IDLE);
  const [projected, setProjected] = useState(IDLE);
  const [plan, setPlan] = useState(IDLE);
  const [payments, setPayments] = useState(IDLE);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const sections = quotaComponentSections(quota);

  useEffect(() => {
    if (!open || !quota) return undefined;
    setPaymentsOpen(false);
    if (creditUuid) {
      loadResource(() => fetchOriginalPaymentPlan(creditUuid), setPlan);
      loadResource(
        () => Promise.all([
          fetchCreditQuotaStatements(creditUuid).then((statements) => {
            const item = (statements || []).find((row) => row?.quota?.uuid === quota.uuid);
            return appliedPaymentRecords(item?.payments);
          }),
          clientUuid
            ? fetchCreditPaymentRecords(clientUuid, creditUuid).catch(() => [])
            : Promise.resolve([]),
        ]).then(([applied, records]) => {
          const byUuid = new Map((records || []).map((record) => [record.uuid, record]));
          return applied.map((payment) => ({
            ...payment,
            paymentRecord: byUuid.get(payment.paymentRecordUuid) || payment.paymentRecord,
          }));
        }),
        setPayments
      );
    } else {
      setPlan({ status: "error", data: null });
      setPayments({ status: "error", data: null });
    }
    loadResource(() => fetchQuotaPaidComponents(quota.uuid), setPaid);
    loadResource(() => fetchQuota(quota.uuid), setQuotaDetail);
    if (sections.showProjected) loadResource(() => fetchQuotaProjectedComponents(quota.uuid), setProjected);
    else setProjected(IDLE);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quota, creditUuid, clientUuid]);

  if (!quota) return null;
  const state = quotaState(quota, today);
  const original = plan.status === "ready" ? originalPlanQuota(plan.data, quota.quotaNumber) : null;
  const generatedQuota = {
    ...quota,
    generatedGac: quotaDetail.status === "ready" ? quotaDetail.data?.generatedGac : quota?.generatedGac,
    interesesMoratoriosGenerados: quotaDetail.status === "ready"
      ? quotaDetail.data?.interesesMoratoriosGenerados
      : quota?.interesesMoratoriosGenerados,
    condonacion: quotaDetail.status === "ready" ? quotaDetail.data?.condonacion : quota?.condonacion,
    overpayment: quotaDetail.status === "ready" ? quotaDetail.data?.overpayment : quota?.overpayment,
  };
  const comparison = quotaComparisonRows(original, generatedQuota, paid.status === "ready" ? paid.data : null);
  const applied = payments.status === "ready" ? payments.data || [] : [];

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Componentes de la cuota"
      subtitle={`Cuota ${String(quota.quotaNumber).padStart(2, "0")}/${quotasCount} · Vence ${formatDate(quota.payDate)}`}
      maxWidth="md"
      actions={<SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2 }}>
        <Box>
          <Typography sx={labelSx}>Estado</Typography>
          <Box sx={{ mt: 0.5 }}><StatusBadge tone={state.tone} label={state.label} /></Box>
        </Box>
        <Box>
          <Typography sx={labelSx}>Valor de cuota</Typography>
          <Typography sx={{ fontWeight: 500 }}>{formatMoney(quota.value)}</Typography>
        </Box>
        <Box>
          <Typography sx={labelSx}>Valor pagado</Typography>
          <Typography sx={{ fontWeight: 500 }}>{formatMoney(quota.paidValue)}</Typography>
        </Box>
        <Box>
          <Typography sx={labelSx}>Saldo de cuota</Typography>
          <Typography sx={{ fontWeight: 500 }}>{formatMoney(quotaBalance(quota))}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
        {plan.status === "loading" && <Skeleton variant="rounded" height={280} />}
        {plan.status === "error" && <Alert severity="error">No pudimos cargar el plan original de esta cuota.</Alert>}
        {plan.status === "ready" && !original && <Alert severity="info">Esta cuota no está en el plan de pagos original.</Alert>}
        {plan.status === "ready" && original && (
          <MoneyTable
            title="Plan original"
            description="Lo estimado para esta cuota en la originación."
            fill="blue"
            rows={comparison.filter((row) => !row.actualOnly).map((row) => ({ label: row.originalLabel, ...comparisonCell(row.type, row.originalValue) }))}
          />
        )}
        <MoneyTable
          title="Aplicación actual"
          description="Cómo quedó registrada la cuota con los pagos aplicados."
          fill="green"
          rows={comparison.map((row) => ({ label: row.actualLabel, bold: row.bold, ...comparisonCell(row.type, row.actualValue) }))}
        />
      </Box>

      {sections.showProjected && (
        <ResourceTable
          title="Componentes proyectados"
          description="Lo que falta por pagar de esta cuota, por componente, calculado a hoy."
          resource={projected}
          keyName="pendingKey"
        />
      )}

      <Box component="section" aria-label="Pagos aplicados">
        <Box
          component="button"
          type="button"
          aria-expanded={paymentsOpen}
          onClick={() => setPaymentsOpen((current) => !current)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            p: 0,
            border: 0,
            background: "none",
            cursor: "pointer",
            textAlign: "left",
            color: COLORS.primary,
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{ display: "inline-flex", color: COLORS.primary, transform: paymentsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}
          >
            <ExpandMoreIcon />
          </Box>
          <Typography component="span" sx={{ fontSize: 14, fontWeight: 700 }}>Pagos aplicados</Typography>
          {payments.status === "ready" && <StatusBadge tone="blue" label={String(applied.length)} />}
        </Box>
        <Collapse in={paymentsOpen}>
          <Box sx={{ pt: 1.5 }}>
            {payments.status === "loading" && <Skeleton variant="rounded" height={72} />}
            {payments.status === "error" && <Alert severity="error">No pudimos cargar los payment records aplicados.</Alert>}
            {payments.status === "ready" && applied.length === 0 && (
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>Esta cuota no tiene payment records aplicados.</Typography>
            )}
            {payments.status === "ready" && applied.length > 0 && (
              <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflowX: "auto" }}>
                <Table size="small" aria-label="Payment records aplicados">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={paymentHeadSx}>ID</TableCell>
                      <TableCell sx={paymentHeadSx}>Fecha</TableCell>
                      <TableCell sx={{ ...paymentHeadSx, ...amountCellSx }}>Valor</TableCell>
                      <TableCell sx={paymentHeadSx}>Recaudador</TableCell>
                      <TableCell sx={paymentHeadSx}>Notas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applied.map((payment, index) => {
                      const details = appliedPaymentDetails(payment);
                      return (
                        <TableRow key={payment.paymentRecordUuid || payment.uuid || details.oldId || index}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{details.oldId ?? "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(paymentWhen(payment))}</TableCell>
                          <TableCell sx={{ ...amountCellSx, whiteSpace: "nowrap" }}>{formatMoney(paymentAmount(payment))}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{details.collector || "—"}</TableCell>
                          <TableCell>
                            {details.notes ? (
                              <Tooltip title={details.notes}>
                                <Typography sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500 }}>
                                  {details.notes}
                                </Typography>
                              </Tooltip>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </ModalShell>
  );
}
